import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, AlertCircle } from '@/components/ui/icons';
import { Button } from './Button';

// THE empty/error/loading states (commandment 14) — every query renders these,
// never ad-hoc spinners or blank divs. Instrument system (Claude Design §1.4):
// list loading is a shimmer skeleton ("no spinners on lists"), empty/error use
// a 44px chip + title/body, error retries via an outline button.

const shimmer: CSSProperties = {
  background: 'linear-gradient(90deg,#EAEEF3 25%,#F6F8FA 50%,#EAEEF3 75%)',
  backgroundSize: '200% 100%',
  animation: 'ds-shimmer 1.4s ease infinite',
};

export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <div className={className} style={{ ...shimmer, ...style }} aria-hidden />;
}

export function LoadingState({ rows = 4 }: { label?: string; rows?: number }) {
  const { t } = useTranslation();
  return (
    <div className="bg-white border border-line" role="status" aria-label={t('loading')}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-5 h-14 border-b border-line-soft last:border-0">
          <Skeleton style={{ height: 12, width: '32%' }} />
          <Skeleton style={{ height: 12, width: '18%' }} />
          <div className="ml-auto">
            <Skeleton style={{ height: 18, width: 70 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ title, body, action, icon }: { title?: string; body?: string; action?: ReactNode; icon?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-center">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-highlight mb-3 text-primary-ink">
        {icon ?? <Inbox className="h-[22px] w-[22px]" aria-hidden />}
      </div>
      <h3 className="text-[14.5px] font-semibold text-ink">{title ?? t('emptyTitle')}</h3>
      {body && <p className="text-[13px] leading-[19px] text-muted mt-1 max-w-sm">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-center" role="alert">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-danger-soft mb-3 text-danger-ink">
        <AlertCircle className="h-[22px] w-[22px]" aria-hidden />
      </div>
      <h3 className="text-[14.5px] font-semibold text-ink">{t('errorTitle')}</h3>
      <p className="text-[13px] leading-[19px] text-muted mt-1 max-w-sm">{message ?? t('errorBody')}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          {t('retry')}
        </Button>
      )}
    </div>
  );
}
